import React, { useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { ref as dbRef } from 'firebase/database';
import { FiBookOpen, FiInbox, FiSearch, FiUser } from 'react-icons/fi';
import { LuBrainCircuit, LuRadar } from 'react-icons/lu';
import { db } from '../../firebaseConfig';
import { getCachedFirebaseValue, subscribeCachedValue } from '../firebaseDataCache';
import { getActivePageKey } from '../navigation';
import { useLanguage } from '../contexts/LanguageContext';
import ProductTutorial from './ProductTutorial';
import { MobileHelpContext } from './MobileHelpSlot';
import useMobileLayout from './useMobileLayout';
import { PlanControlSlot, UpgradeExperienceProvider } from '../upgrade/UpgradeExperience';

const TOP_NAV_ITEMS = [
  {
    key: 'opportunities',
    labelKey: 'navigation.items.opportunities',
    to: '/opportunities',
    icon: FiSearch,
    color: 'var(--js-nav-blue)',
  },
  {
    key: 'career-coach',
    labelKey: 'navigation.items.careerCoach',
    to: '/career-coach',
    icon: LuBrainCircuit,
    color: 'var(--js-nav-purple)',
  },
  {
    key: 'learning-pathways',
    labelKey: 'navigation.items.learningPathways',
    to: '/learning-pathways',
    icon: FiBookOpen,
    color: 'var(--js-nav-yellow)',
  },
  {
    key: 'company-radar',
    labelKey: 'navigation.items.companyRadar',
    to: '/company-radar',
    icon: LuRadar,
    color: 'var(--js-nav-green)',
  },
  {
    key: 'inbox',
    labelKey: 'navigation.items.inbox',
    to: '/inbox',
    icon: FiInbox,
    color: 'var(--js-nav-neutral)',
  },
  {
    key: 'profile',
    labelKey: 'navigation.items.profile',
    to: '/profile',
    icon: FiUser,
    color: 'var(--js-nav-neutral)',
  },
];

function JobSeekerShell({ user, profile, onProfileSaved }) {
  const location = useLocation();
  const mobile = useMobileLayout();
  const [helpTarget, setHelpTarget] = useState(null);
  const { t, language } = useLanguage();
  const mobileLabels = language === 'ar'
    ? ['وظائف', 'مدرّب', 'تعلّم', 'شركات', 'الوارد', 'حسابي']
    : ['Jobs', 'Coach', 'Learn', 'Radar', 'Inbox', 'Profile'];
  const pathwayPath = user?.uid ? `people/${user.uid}/learningPathway` : '';
  const cachedPathway = pathwayPath ? getCachedFirebaseValue(pathwayPath) : undefined;
  const [learningPathwayRecord, setLearningPathwayRecord] = useState(cachedPathway || null);
  const activeKey = getActivePageKey(location.pathname);
  const isJobWorkspaceView = location.pathname.startsWith('/job/');
  const isAskAnythingView = location.pathname.startsWith('/ask-anything');
  const isConfigureScoutView = location.pathname.startsWith('/configure-scout');
  const isCareerPathsView = location.pathname.startsWith('/career-paths');
  const isCompanyRadarDetailView = /^\/company-radar\/[^/]+/.test(location.pathname);
  const isFocusedApplyView = (
    isJobWorkspaceView
    || location.pathname.startsWith('/mock-interview')
    || isAskAnythingView
    || isConfigureScoutView
    || isCareerPathsView
    || isCompanyRadarDetailView
  );
  const learningPathwayNavStatus = useMemo(() => {
    if (!learningPathwayRecord) return null;
    if (learningPathwayRecord.generationStatus === 'in_progress') {
      return { label: t('navigation.status.building'), tone: 'loading' };
    }
    if (learningPathwayRecord.generationStatus === 'completed' && !learningPathwayRecord.onboardingCompleted) {
      return { label: t('navigation.status.ready'), tone: 'ready' };
    }
    return null;
  }, [learningPathwayRecord, t]);

  useEffect(() => {
    if (!user?.uid) {
      setLearningPathwayRecord(null);
      return undefined;
    }
    const pathwayRef = dbRef(db, pathwayPath);
    return subscribeCachedValue(pathwayRef, pathwayPath, (snapshot) => {
      setLearningPathwayRecord(snapshot.exists() ? snapshot.val() : null);
    });
  }, [user?.uid, pathwayPath]);

  return (
    <UpgradeExperienceProvider user={user} profile={profile} onProfileSaved={onProfileSaved}>
    <MobileHelpContext.Provider value={setHelpTarget}>
    <div className={`js-shell ${isJobWorkspaceView ? 'js-shell--job-workspace' : ''} ${isFocusedApplyView ? 'js-shell--focus' : ''} ${isAskAnythingView || isConfigureScoutView || isCompanyRadarDetailView ? 'js-shell--ama-focus' : ''}`}>
      {!isFocusedApplyView && (
        <header className="js-topbar">
          <p className="js-topbar__brand" dir="ltr" aria-label={t('common.home')}>
            <span>JOBS</span>
            <span>EEQR</span>
          </p>

          <nav className="js-topbar__nav" aria-label={t('navigation.workspaceLabel')}>
            {TOP_NAV_ITEMS.map((item, index) => {
              const Icon = item.icon;
              const active = item.key === activeKey;
              const needsDivider = index === 4;
              const navStatus = item.key === 'learning-pathways' ? learningPathwayNavStatus : null;

              return (
                <React.Fragment key={item.key}>
                  {needsDivider && <span className="js-topbar__divider" aria-hidden="true" />}
                  <Link
                    to={item.to}
                    aria-label={navStatus ? `${t(item.labelKey)} · ${navStatus.label}` : t(item.labelKey)}
                    aria-current={active ? 'page' : undefined}
                    className={`js-topbar__item ${active ? 'is-active' : ''}`}
                    style={{ '--item-color': item.color }}
                  >
                    <span className="js-topbar__item-inner">
                      <Icon />
                      <span className="js-topbar__item-label">{t(item.labelKey)}</span>
                      <span className="js-topbar__mobile-label" aria-hidden="true">{mobileLabels[index]}</span>
                      {navStatus && (
                        <span className={`js-topbar__status is-${navStatus.tone}`}>
                          {navStatus.label}
                        </span>
                      )}
                    </span>
                  </Link>
                </React.Fragment>
              );
            })}
          </nav>
          <PlanControlSlot />
        </header>
      )}

      {!isFocusedApplyView && <div className="js-mobile-planbar"><PlanControlSlot /></div>}
      <main className="js-main">
        <div className="js-page-frame">
          <Outlet />
        </div>
      </main>
      <ProductTutorial user={user} profile={profile} onProfileSaved={onProfileSaved} helpTarget={mobile ? helpTarget : null} />
    </div>
    </MobileHelpContext.Provider>
    </UpgradeExperienceProvider>
  );
}

export default JobSeekerShell;
